import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tag, X, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { it as itLocale, enUS, de, fr } from "date-fns/locale";

const STORAGE_KEY = "bb_promo_dismissed_v1";

const localeFor = (lang) => ({ it: itLocale, en: enUS, de, fr }[lang] || itLocale);

const PromoPopup = () => {
  const { t, lang } = useLang();
  const [promo, setPromo] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    // Skip if user already dismissed today
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const ts = parseInt(dismissedAt, 10);
      if (!Number.isNaN(ts) && Date.now() - ts < 24 * 60 * 60 * 1000) return;
    }
    api
      .get("/promotions/active")
      .then((res) => {
        const list = res.data?.promotions || [];
        if (!alive || list.length === 0) return;
        setPromo(list[0]);
        // Smooth entrance
        setTimeout(() => setOpen(true), 1500);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const close = () => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  };

  if (!promo || !open) return null;

  const loc = localeFor(lang);
  const fromLabel = format(new Date(promo.from_date), "dd/MM/yyyy");
  const toLabel = format(new Date(promo.to_date), "dd/MM/yyyy");
  const typeBadge = {
    last_minute: t.promo.type_last_minute,
    seasonal: t.promo.type_seasonal,
    special: t.promo.type_special,
  }[promo.type] || t.promo.type_special;

  return (
    <div
      className="fixed bottom-20 left-5 z-[60] max-w-sm animate-fadeUp"
      role="dialog"
      aria-live="polite"
      data-testid="promo-popup"
    >
      <div className="relative bg-white rounded-2xl border border-fuchsia-200 shadow-[0_18px_50px_-12px_rgba(192,38,211,0.35)] p-5 pr-10">
        <button
          type="button"
          onClick={close}
          aria-label="Chiudi"
          data-testid="promo-popup-close"
          className="absolute top-3 right-3 text-[#635F59] hover:text-[#1E3A5F] transition"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-fuchsia-600 text-white text-[10px] font-bold uppercase tracking-wider">
            <Tag className="w-3 h-3" /> {typeBadge}
          </span>
          {promo.discount_pct ? (
            <span className="text-fuchsia-700 font-bold text-sm">
              -{Math.round(promo.discount_pct)}%
            </span>
          ) : null}
        </div>

        <h3 className="font-display text-xl text-[#1E3A5F] leading-tight mb-1">
          {promo.label}
        </h3>
        <p className="text-xs text-[#635F59] mb-3">
          {t.promo.valid_from} <strong className="text-[#1E3A5F]">{fromLabel}</strong> {t.promo.valid_to}{" "}
          <strong className="text-[#1E3A5F]">{toLabel}</strong>
          {promo.from_price != null && (
            <>
              {" · "}
              {t.promo.from_price} <strong className="text-[#AC5D45]">€{promo.from_price}</strong>
            </>
          )}
        </p>

        <div className="flex items-center gap-2">
          <Button
            asChild
            size="sm"
            data-testid="promo-popup-cta"
            className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full px-4 h-9 text-xs"
          >
            <Link to="/booking" onClick={close}>
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              {t.promo.cta}
            </Link>
          </Button>
          <button
            type="button"
            onClick={close}
            className="text-xs text-[#635F59] hover:text-[#1E3A5F] underline-offset-2 hover:underline"
            data-testid="promo-popup-later"
          >
            {t.promo.later}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoPopup;
